import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Lock, MapPin, Plus, Trash2, Truck, Wrench, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { fetchDiscounts, fetchProducts, fetchStoreConfig, formatNZD, submitOrder } from "@/lib/api";
import { toast } from "sonner";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  street: "",
  suburb: "",
  city: "",
  region: "",
  postcode: "",
  notes: "",
  dropoff_date: "",
  dropoff_time: "",
};

export default function Checkout() {
  const { items, subtotal, clearCart, addItem, removeItem } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [delivery, setDelivery] = useState("ship");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null);
  const [codeError, setCodeError] = useState("");

  useEffect(() => {
    fetchStoreConfig().then(setConfig).catch(() => setConfig(null));
    fetchProducts().then(setProducts).catch(() => setProducts([]));
    fetchDiscounts().then(setDiscounts).catch(() => setDiscounts([]));
  }, []);

  const hasKit = items.some((i) => (i.badge ?? "Combo Kit") === "Combo Kit");

  if (items.length === 0 && !submitting) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-start justify-center gap-6 px-5 sm:px-8">
        <h1 className="font-display text-3xl font-extrabold text-ink" data-testid="checkout-empty-heading">
          Nothing to check out yet.
        </h1>
        <p className="text-sm text-zinc-600">Add a kit to your cart first.</p>
        <Link to="/shop" data-testid="checkout-empty-shop" className="rounded-[3px] bg-volt px-6 py-3 font-bold text-white">
          Shop Kits
        </Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.street.trim() && delivery === "ship") errs.street = "Required";
    if (!form.city.trim() && delivery === "ship") errs.city = "Required";
    if (delivery === "installation" && !form.dropoff_date) errs.dropoff_date = "Pick a drop-off date";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const installationFee =
    delivery === "installation" ? config?.installation_fee || 0 : 0;
  const baseShipping = config
    ? delivery === "installation"
      ? 0
      : hasKit
        ? config.shipping_flat_rate
        : config.accessory_shipping_flat
    : 0;
  const shippingDiscount =
    appliedCode && appliedCode.type === "percent_shipping"
      ? Math.round(baseShipping * appliedCode.value) / 100
      : 0;
  const shipping = Math.max(0, baseShipping - shippingDiscount);
  const total = subtotal + shipping + installationFee;

  const applyCode = () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    const match = discounts.find((d) => d.code === code);
    if (!match) {
      setAppliedCode(null);
      setCodeError("Invalid code");
      return;
    }
    setAppliedCode(match);
    setCodeError("");
    setCodeInput("");
    toast.success(`${code} applied`, {
      description: `${match.value}% off shipping`,
    });
  };

  const addProductDefaults = (p) => {
    const defaults = {};
    (p.options || []).forEach((g) => (defaults[g.id] = g.choices[0].id));
    addItem({
      type: "product",
      productId: p.id,
      badge: p.badge,
      name: p.name,
      image: p.images[0],
      unitPrice: p.base_price,
      qty: 1,
      options: defaults,
      detailLines: (p.options || []).map((g) => {
        const c = g.choices[0];
        return `${g.label}: ${c.name}`;
      }),
    });
    toast.success(`${p.name} added`, {
      description: `Default spec — ${formatNZD(p.base_price)}. Change options from its product page if you like.`,
    });
  };

  const handleRemove = (item) => {
    removeItem(item.key);
    toast.success(`${item.name} removed`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty", {
        description: "Add a kit or accessory before placing the order.",
      });
      return;
    }
    if (!validate()) {
      toast.error("Check the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const order = await submitOrder({
        customer: { name: form.name, email: form.email, phone: form.phone },
        shipping_address: {
          street: form.street,
          suburb: form.suburb,
          city: form.city,
          region: form.region,
          postcode: form.postcode,
        },
        items: items.map((i) => ({
          type: i.type,
          product_id: i.productId || null,
          options: i.options || null,
          qty: i.qty,
        })),
        delivery,
        discount_code: appliedCode?.code || null,
        dropoff_date: delivery === "installation" ? form.dropoff_date : null,
        dropoff_time: delivery === "installation" ? form.dropoff_time || null : null,
        notes: form.notes || null,
      });
      clearCart();
      navigate(`/order-confirmation/${order.id}`);
    } catch (err) {
      setSubmitting(false);
      toast.error("Order failed to submit", {
        description: "Please try again, or reach us through the contact form.",
      });
    }
  };

  const field = (key, label, opts = {}) => (
    <div className={opts.span || ""}>
      <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
        {label} {opts.required ? <span className="text-volt">*</span> : <span className="text-zinc-400">(optional)</span>}
      </label>
      <input
        type={opts.type || "text"}
        value={form[key]}
        onChange={set(key)}
        data-testid={`checkout-${key.replace(/_/g, "-")}-input`}
        className={`w-full rounded-[3px] border bg-white px-4 py-3 text-sm text-ink placeholder:text-zinc-400 transition-colors duration-200 focus:border-volt focus:outline-none ${
          errors[key] ? "border-red-500" : "border-black/20"
        }`}
        placeholder={opts.placeholder || ""}
      />
      {errors[key] && (
        <p className="mt-1 text-xs text-red-500" data-testid={`checkout-${key.replace(/_/g, "-")}-error`}>
          {errors[key]}
        </p>
      )}
    </div>
  );

  const DELIVERY_OPTIONS = [
    {
      id: "ship",
      icon: Truck,
      title: "Ship to my door",
      note: hasKit
        ? "Couried to your address — $170 flat, NZ-wide, already in your total."
        : "Couried to your address — $10 flat, already in your total.",
    },
    {
      id: "auckland",
      icon: MapPin,
      title: "Auckland pickup / drop-off",
      note: hasKit
        ? "Pick it up from us in Auckland, or we drop it off — same $170 flat."
        : "Pick it up from us in Auckland, or we drop it off — same $10 flat.",
    },
    ...(hasKit
      ? [
          {
            id: "installation",
            icon: Wrench,
            title: "Auckland installation",
            note: `Drop your bike to us, or have the kit shipped straight to us — we build it and you pick up the finished ride. ${formatNZD(config?.installation_fee ?? 200)} flat, no shipping fee.`,
          },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <section className="py-14 sm:py-16">
        <p className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-volt" />
          Checkout
        </p>
        <h1 className="font-display text-4xl font-black tracking-tight text-ink sm:text-5xl" data-testid="checkout-heading">
          ALMOST RIDING.
        </h1>
        <p className="mt-3 max-w-xl text-sm text-zinc-600">
          Three steps: your details, how you want it delivered, place the
          order. We email you a manual invoice — no card needed now.
        </p>
      </section>

      <form onSubmit={handleSubmit} noValidate className="grid gap-10 lg:grid-cols-3" data-testid="checkout-form">
        <div className="min-w-0 space-y-10 lg:col-span-2">
          <Reveal>
            <fieldset>
              <legend className="mb-5 font-display text-xl font-extrabold text-ink">
                <span className="mr-3 font-mono text-sm text-volt">01</span> Contact
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {field("name", "Full name", { required: true, span: "sm:col-span-2" })}
                {field("email", "Email", { required: true, type: "email" })}
                {field("phone", "Phone")}
              </div>
            </fieldset>
          </Reveal>

          <Reveal>
            <fieldset>
              <legend className="mb-5 font-display text-xl font-extrabold text-ink">
                <span className="mr-3 font-mono text-sm text-volt">02</span> Delivery
              </legend>
              <div className="grid gap-3 sm:grid-cols-3" data-testid="checkout-delivery-options">
                {DELIVERY_OPTIONS.map((d) => {
                  const active = delivery === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-testid={`checkout-delivery-${d.id}`}
                      onClick={() => setDelivery(d.id)}
                      className={`flex items-start gap-4 rounded-xl border p-5 text-left transition-colors duration-200 ${
                        active ? "border-volt bg-volt/5" : "border-black/10 bg-white hover:border-black/30"
                      }`}
                    >
                      <d.icon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? "text-volt" : "text-zinc-400"}`} strokeWidth={2.2} />
                      <span>
                        <span className="flex items-center gap-2 font-display text-sm font-bold text-ink">
                          {d.title}
                          {active && (
                            <span className="rounded-full bg-volt px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white">
                              Chosen
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block text-xs leading-relaxed text-zinc-500">{d.note}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs leading-relaxed text-zinc-500">
                {delivery === "installation"
                  ? `The ${formatNZD(installationFee)} installation covers everything — drop-off, build and pick-up.`
                  : `Same flat fee either way — the ${hasKit ? "$170" : "$10"} in your total covers every option.`}
              </p>
              {delivery === "installation" && (
                <div
                  className="mt-5 grid gap-4 rounded-xl border border-volt/30 bg-volt/5 p-5 sm:grid-cols-2"
                  data-testid="checkout-dropoff-booking"
                >
                  <label>
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                      Preferred drop-off date <span className="text-volt">*</span>
                    </span>
                    <input
                      type="date"
                      min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                      value={form.dropoff_date}
                      onChange={set("dropoff_date")}
                      data-testid="checkout-dropoff-date-input"
                      className={`w-full rounded-[3px] border bg-white px-4 py-3 text-sm text-ink transition-colors duration-200 focus:border-volt focus:outline-none ${
                        errors.dropoff_date ? "border-red-500" : "border-black/20"
                      }`}
                    />
                    {errors.dropoff_date && (
                      <p className="mt-1 text-xs text-red-500" data-testid="checkout-dropoff-date-error">
                        {errors.dropoff_date}
                      </p>
                    )}
                  </label>
                  <label>
                    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                      Preferred time <span className="text-zinc-400">(optional)</span>
                    </span>
                    <select
                      value={form.dropoff_time}
                      onChange={set("dropoff_time")}
                      data-testid="checkout-dropoff-time-input"
                      className="w-full rounded-[3px] border border-black/20 bg-white px-4 py-3 text-sm text-ink transition-colors duration-200 focus:border-volt focus:outline-none"
                    >
                      <option value="">Any time</option>
                      <option value="Morning (8am–12pm)">Morning (8am–12pm)</option>
                      <option value="Afternoon (12–5pm)">Afternoon (12–5pm)</option>
                      <option value="Evening (5–8pm)">Evening (5–8pm)</option>
                    </select>
                  </label>
                  <p className="text-xs leading-relaxed text-zinc-500 sm:col-span-2">
                    Drop-off booking only — we'll sort out picking up your
                    finished build together once it's done.
                  </p>
                </div>
              )}
            </fieldset>
          </Reveal>

          <Reveal>
            <fieldset>
              <legend className="mb-5 font-display text-xl font-extrabold text-ink">
                <span className="mr-3 font-mono text-sm text-volt">03</span> Address
              </legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {field("street", "Street address", { required: delivery === "ship", span: "sm:col-span-2", placeholder: "123 Queen Street" })}
                {field("suburb", "Suburb")}
                {field("city", "City / Town", { required: delivery === "ship" })}
                {field("region", "Region")}
                {field("postcode", "Postcode")}
              </div>
              {delivery !== "ship" && (
                <p className="mt-3 text-xs leading-relaxed text-zinc-500">
                  Leave your address anyway — we'll use it to arrange a pickup,
                  drop-off or installation time that suits you.
                </p>
              )}
            </fieldset>
          </Reveal>

          <Reveal>
            <fieldset>
              <legend className="mb-5 font-display text-xl font-extrabold text-ink">
                <span className="mr-3 font-mono text-sm text-volt">04</span> Order Notes
              </legend>
              <textarea
                value={form.notes}
                onChange={set("notes")}
                rows={4}
                data-testid="checkout-notes-input"
                placeholder="Bike model, front or rear motor, anything else we should know…"
                className="w-full rounded-[3px] border border-black/20 bg-white px-4 py-3 text-sm text-ink placeholder:text-zinc-400 transition-colors duration-200 focus:border-volt focus:outline-none"
              />
            </fieldset>
          </Reveal>
        </div>

        {/* ---------- ORDER REVIEW ---------- */}
        <div className="min-w-0 self-start lg:sticky lg:top-24">
          <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="checkout-review">
            <div className="flex items-center justify-between gap-3">
              <p className="eyebrow">Order Review</p>
              <button
                type="button"
                data-testid="checkout-add-kit-toggle"
                onClick={() => setAddPanelOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-[3px] border border-volt/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-volt transition-colors duration-200 hover:bg-volt hover:text-white"
              >
                {addPanelOpen ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                {addPanelOpen ? "Close" : "Add kits"}
              </button>
            </div>

            {addPanelOpen && (
              <div className="mt-4 rounded-xl border border-black/10 bg-surface p-3" data-testid="checkout-add-panel">
                <p className="px-1 pb-2 text-[11px] leading-relaxed text-zinc-500">
                  Added at the default spec — tweak options on the product page
                  later if you like.
                </p>
                <ul className="space-y-1.5">
                  {products.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        data-testid={`checkout-add-product-${p.slug}`}
                        onClick={() => addProductDefaults(p)}
                        className="flex w-full items-center gap-3 rounded-lg border border-transparent bg-white p-2 text-left transition-colors duration-200 hover:border-volt/40"
                      >
                        <img src={p.images[0]} alt="" className="h-9 w-9 shrink-0 rounded-md object-cover" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-bold text-ink">{p.name}</span>
                          <span className="block font-mono text-[10px] text-zinc-500">{formatNZD(p.base_price)}</span>
                        </span>
                        <Plus className="h-4 w-4 shrink-0 text-volt" strokeWidth={2.5} />
                      </button>
                    </li>
                  ))}
                  {products.length === 0 && (
                    <li className="px-1 text-xs text-zinc-500">Loading products…</li>
                  )}
                </ul>
              </div>
            )}

            <ul className="mt-5 space-y-4" data-testid="checkout-review-items">
              {items.map((item, i) => (
                <li key={item.key} className="flex gap-3" data-testid={`checkout-item-${i}`}>
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-black/10 bg-surface">
                    <img src={item.image} alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-ink">
                      {item.name} <span className="text-zinc-500">× {item.qty}</span>
                    </p>
                    {item.detailLines && (
                      <p className="mt-0.5 truncate text-[11px] text-zinc-500">
                        {item.detailLines.join(" · ")}
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 font-mono text-sm text-zinc-700">
                    {formatNZD(item.unitPrice * item.qty)}
                  </p>
                  <button
                    type="button"
                    data-testid={`checkout-item-${i}-remove`}
                    aria-label={`Remove ${item.name}`}
                    onClick={() => handleRemove(item)}
                    className="shrink-0 self-start rounded-[3px] border border-black/10 p-1.5 text-zinc-400 transition-colors duration-200 hover:border-red-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
              {items.length === 0 && (
                <li className="text-xs text-zinc-500" data-testid="checkout-items-empty">
                  Nothing here — add kits above or head back to the cart.
                </li>
              )}
            </ul>

            <div className="mt-5 border-t border-black/10 pt-4" data-testid="checkout-discount">
              {appliedCode ? (
                <div
                  className="flex items-center justify-between rounded-lg bg-volt/5 px-3 py-2.5"
                  data-testid="checkout-discount-applied"
                >
                  <span className="flex items-center gap-2 text-xs font-bold text-volt">
                    <Check className="h-3.5 w-3.5" />
                    {appliedCode.code} — {appliedCode.value}% off shipping
                  </span>
                  <button
                    type="button"
                    onClick={() => setAppliedCode(null)}
                    aria-label="Remove discount code"
                    data-testid="checkout-discount-remove"
                  >
                    <X className="h-3.5 w-3.5 text-zinc-400 transition-colors duration-200 hover:text-ink" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      value={codeInput}
                      onChange={(e) => {
                        setCodeInput(e.target.value);
                        setCodeError("");
                      }}
                      placeholder="Discount code"
                      data-testid="checkout-discount-input"
                      className={`w-full rounded-[3px] border bg-white px-4 py-2.5 font-mono text-sm uppercase tracking-wide text-ink transition-colors duration-200 placeholder:font-sans placeholder:normal-case placeholder:tracking-normal placeholder:text-zinc-400 focus:border-volt focus:outline-none ${
                        codeError ? "border-red-500" : "border-black/20"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={applyCode}
                      data-testid="checkout-discount-apply"
                      className="shrink-0 rounded-[3px] border border-black/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 transition-colors duration-200 hover:border-volt hover:text-volt"
                    >
                      Apply
                    </button>
                  </div>
                  {codeError && (
                    <p className="mt-1.5 text-xs text-red-500" data-testid="checkout-discount-error">
                      {codeError}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="mt-6 space-y-3 border-t border-black/10 pt-5 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">Subtotal</span>
                <span className="font-mono text-ink" data-testid="checkout-subtotal">{formatNZD(subtotal)}</span>
              </div>
              {delivery !== "installation" && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">
                    Delivery ({delivery === "auckland" ? "Auckland pickup/drop-off" : "ship to door"})
                    {shippingDiscount > 0 && appliedCode && (
                      <span className="block font-mono text-[10px] font-bold text-volt" data-testid="checkout-discount-row">
                        {appliedCode.code} — {appliedCode.value}% off shipping
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-ink" data-testid="checkout-shipping">{formatNZD(shipping)}</span>
                </div>
              )}
              {delivery === "installation" && (
                <div className="flex justify-between">
                  <span className="text-zinc-600 flex items-center gap-1.5">
                    <Wrench className="h-3.5 w-3.5 text-volt" /> Installation (we build it)
                  </span>
                  <span className="font-mono text-ink" data-testid="checkout-installation-fee">
                    {formatNZD(installationFee)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-black/10 pt-4">
                <span className="font-display font-bold text-zinc-600">Total</span>
                <span className="font-mono text-xl font-bold text-volt" data-testid="checkout-total">
                  {formatNZD(total)}
                </span>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-black/10 bg-surface p-4">
              <p className="flex items-center gap-2 text-xs font-bold text-ink" data-testid="checkout-payment-note">
                <Lock className="h-3.5 w-3.5 text-volt" /> No card needed now
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-zinc-500">
                We confirm stock with our supplier, then email you a manual
                invoice. Your kit ships (or gets picked up) once it's paid.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting || items.length === 0}
              data-testid="checkout-place-order"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-[3px] bg-volt px-6 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Placing order…
                </>
              ) : (
                "Place Order"
              )}
            </button>
            <Link
              to="/cart"
              data-testid="checkout-back-to-cart"
              className="mt-4 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-200 hover:text-volt"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to cart
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
